import { useEffect, useRef, useState } from "react";
import { useNotify } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Settings → Scripts (docs/16 §G, D34). Configurable caller scripts — NOT
// hard-coded copy. Admins author named scripts; the caller picks one on the lead
// page mid-call (see LeadShow) and it renders against the live lead. Near-clone of
// TemplatesPage: getSupabaseClient() CRUD + merge-field picker + render_template
// preview. Write-gated by manage_templates_tags (RLS, migration 0027).

// Same tokens render_template() resolves (migration 0013) — surfaced verbatim.
const MERGE_FIELDS = [
  { token: "{{first_name}}", desc: "Lead's first name (→ 'there' if unknown)" },
  { token: "{{property_address}}", desc: "Property street address" },
  { token: "{{city}}", desc: "Property city" },
];
// Seeded type suggestions (a datalist — free text still allowed, no CHECK on the column).
const TYPE_SUGGESTIONS = ["opener", "umbc", "road_block", "voicemail", "other"];

type Script = {
  id: string;
  name: string;
  body: string;
  script_type: string | null;
  lead_type: string | null;
  is_active: boolean;
  sort_order: number;
};
type Draft = { id?: string; name: string; body: string; script_type: string; lead_type: string; is_active: boolean; sort_order: number };
const EMPTY: Draft = { name: "", body: "", script_type: "opener", lead_type: "", is_active: true, sort_order: 0 };

const ScriptsPage = () => {
  const notify = useNotify();
  const [rows, setRows] = useState<Script[]>([]);
  const [filter, setFilter] = useState<string>("all");
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [preview, setPreview] = useState<string | null>(null);
  const [sampleLead, setSampleLead] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const load = async () => {
    const { data } = await getSupabaseClient().from("scripts").select("*").order("sort_order").order("name");
    setRows((data as Script[]) ?? []);
  };
  useEffect(() => {
    load();
    getSupabaseClient().from("leads").select("id").limit(1).maybeSingle().then(({ data }) => {
      if (data) setSampleLead((data as any).id);
    });
  }, []);

  const edit = (s: Script) => {
    setDraft({ id: s.id, name: s.name, body: s.body, script_type: s.script_type ?? "", lead_type: s.lead_type ?? "", is_active: s.is_active, sort_order: s.sort_order });
    setPreview(null);
  };

  const insertToken = (token: string) => {
    const el = bodyRef.current;
    if (!el) { setDraft((d) => ({ ...d, body: d.body + token })); return; }
    const start = el.selectionStart ?? draft.body.length;
    const end = el.selectionEnd ?? draft.body.length;
    setDraft((d) => ({ ...d, body: d.body.slice(0, start) + token + d.body.slice(end) }));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; });
  };

  const runPreview = async () => {
    if (!sampleLead) { notify("No lead available to preview against — add a lead first.", { type: "warning" }); return; }
    const { data, error } = await getSupabaseClient().rpc("render_template", { p_body: draft.body, p_lead_id: sampleLead });
    if (error) { notify(`Preview failed: ${error.message}`, { type: "error" }); return; }
    setPreview((data as string) ?? "");
  };

  const save = async () => {
    if (!draft.name.trim() || !draft.body.trim()) { notify("Name and body are required.", { type: "warning" }); return; }
    const payload = {
      name: draft.name.trim(),
      body: draft.body,
      script_type: draft.script_type.trim() || null,
      lead_type: draft.lead_type.trim() || null,
      is_active: draft.is_active,
      sort_order: draft.sort_order,
    };
    const sb = getSupabaseClient();
    const { error } = draft.id
      ? await sb.from("scripts").update(payload).eq("id", draft.id)
      : await sb.from("scripts").insert(payload);
    if (error) { notify(`${draft.id ? "Save" : "Create"} failed: ${error.message}`, { type: "error" }); return; }
    notify(draft.id ? "Script saved" : "Script created", { type: "success" });
    setDraft(EMPTY); setPreview(null); load();
  };

  const del = async (s: Script) => {
    if (!window.confirm(`Delete script "${s.name}"?`)) return;
    const { error } = await getSupabaseClient().from("scripts").delete().eq("id", s.id);
    if (error) notify(`Failed: ${error.message}`, { type: "error" });
    else { notify("Script deleted", { type: "success" }); if (draft.id === s.id) setDraft(EMPTY); load(); }
  };

  const types = Array.from(new Set(rows.map((r) => r.script_type).filter(Boolean))) as string[];
  const visible = rows.filter((r) => filter === "all" || r.script_type === filter);

  return (
    <div className="p-2 max-w-5xl">
      <h1 className="text-xl font-semibold">Scripts</h1>
      <div className="text-sm text-muted-foreground mb-4">
        Caller scripts with merge fields — authored here, picked on the lead page mid-call. Preview resolves the tokens via the live render engine.
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-base">Scripts</CardTitle>
            <div className="flex items-center gap-2">
              <select className="border rounded px-2 py-1 text-sm bg-background" value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="all">All types</option>
                {types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <Button size="sm" variant="outline" onClick={() => { setDraft(EMPTY); setPreview(null); }}>New</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-1">
            {visible.map((s) => (
              <div key={s.id} className={`flex items-center gap-2 rounded px-2 py-1.5 text-sm border-b last:border-0 cursor-pointer hover:bg-muted/50 ${draft.id === s.id ? "bg-muted" : ""}`} onClick={() => edit(s)}>
                {s.script_type && <Badge variant="outline" className="uppercase text-[10px]">{s.script_type}</Badge>}
                <span className="font-medium">{s.name}</span>
                {s.lead_type && <Badge variant="secondary" className="text-[10px]">{s.lead_type}</Badge>}
                {!s.is_active && <Badge variant="outline" className="text-[10px] text-muted-foreground">inactive</Badge>}
                <span className="text-xs text-muted-foreground truncate">{s.body}</span>
                <Button variant="ghost" size="sm" className="ml-auto shrink-0" onClick={(e) => { e.stopPropagation(); del(s); }}>Delete</Button>
              </div>
            ))}
            {visible.length === 0 && <div className="text-sm text-muted-foreground py-2">No scripts{filter !== "all" ? ` of type ${filter}` : ""} yet.</div>}
          </CardContent>
        </Card>

        {/* Editor */}
        <Card>
          <CardHeader><CardTitle className="text-base">{draft.id ? "Edit script" : "New script"}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Name</span>
              <Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="e.g. Probate opener" />
            </label>
            <div className="flex gap-3">
              <label className="flex flex-col gap-1 flex-1">
                <span className="text-xs text-muted-foreground">Type</span>
                <Input list="script-types" value={draft.script_type} onChange={(e) => setDraft((d) => ({ ...d, script_type: e.target.value }))} placeholder="opener / umbc / …" />
                <datalist id="script-types">{TYPE_SUGGESTIONS.map((t) => <option key={t} value={t} />)}</datalist>
              </label>
              <label className="flex flex-col gap-1 flex-1">
                <span className="text-xs text-muted-foreground">Lead type <span className="opacity-60">(optional tag)</span></span>
                <Input value={draft.lead_type} onChange={(e) => setDraft((d) => ({ ...d, lead_type: e.target.value }))} placeholder="probate / foreclosure / …" />
              </label>
              <label className="flex flex-col gap-1 w-20">
                <span className="text-xs text-muted-foreground">Order</span>
                <Input type="number" value={draft.sort_order} onChange={(e) => setDraft((d) => ({ ...d, sort_order: +e.target.value }))} />
              </label>
            </div>

            <label className="flex items-center gap-2">
              <Switch checked={draft.is_active} onCheckedChange={(v) => setDraft((d) => ({ ...d, is_active: v }))} />
              <span className="text-sm">Active <span className="text-xs text-muted-foreground">(shown in the caller's picker)</span></span>
            </label>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Body</span>
                <div className="flex flex-wrap gap-1">
                  {MERGE_FIELDS.map((m) => (
                    <button key={m.token} type="button" title={m.desc} onClick={() => insertToken(m.token)} className="text-[11px] rounded border px-1.5 py-0.5 hover:bg-muted font-mono">{m.token}</button>
                  ))}
                </div>
              </div>
              <Textarea ref={bodyRef} rows={7} value={draft.body} onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))} placeholder="Script the caller reads… click a merge field to insert it" />
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={save}>{draft.id ? "Save" : "Create"}</Button>
              <Button variant="outline" onClick={runPreview} disabled={!draft.body.trim()}>Preview</Button>
              {draft.id && <Button variant="ghost" onClick={() => { setDraft(EMPTY); setPreview(null); }}>Cancel</Button>}
            </div>

            {preview != null && (
              <div className="rounded border bg-muted/40 p-2">
                <div className="text-xs text-muted-foreground mb-1">Preview (resolved against a sample lead)</div>
                <div className="text-sm whitespace-pre-wrap">{preview || <span className="text-muted-foreground">(empty)</span>}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

ScriptsPage.path = "/settings/scripts";
export default ScriptsPage;
